import { Map as MapIcon, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import type { DatasetState } from "@/lib/api";
import { adminApi } from "@/lib/api";
import { formatTimestamp } from "@/lib/format";
import { NewMazeDialog } from "@/routes/admin/mazes/new-maze-dialog";

const DATASET_LABEL: Record<DatasetState, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  none: { label: "no dataset", variant: "outline" },
  recording: { label: "recording…", variant: "secondary" },
  done: { label: "dataset", variant: "default" },
  failed: { label: "recording failed", variant: "destructive" },
};

/**
 * The maze library.
 *
 * Mazes are made here, not on a challenge: a dry-run and its final can share one, a maze can
 * be generated and looked at before any challenge exists, and a challenge can be created
 * before anyone has decided which maze it runs in. Each row says which challenges use it and
 * whether students have anything to download yet.
 *
 * Not scoped to a course: a maze belongs to nobody until a challenge points at it.
 */
export function AdminMazesRoute() {
  const adminKey = useAdminKey();
  const [creating, setCreating] = useState(false);
  const mazes = useAsync(adminKey ? () => adminApi.mazes(adminKey) : null, [adminKey]);

  if (!adminKey) return <EmptyState title="Sign in as admin" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mazes</h1>
          <p className="text-muted-foreground text-sm">
            Scenes the simulation worker serves. Generate one, draw exploration routes, record the
            dataset, then point a challenge at it.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          New maze
        </Button>
      </div>

      <AsyncBoundary
        state={mazes}
        empty={
          <EmptyState
            title="No mazes yet"
            description="A server-simulated challenge needs one. Generate a maze from a seed; the same seed gives the same maze everywhere."
            action={
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" aria-hidden />
                New maze
              </Button>
            }
          />
        }
      >
        {(rows) => (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Maze</TableHead>
                  <TableHead>Grid</TableHead>
                  <TableHead>Camera</TableHead>
                  <TableHead>Dataset</TableHead>
                  <TableHead>Used by</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((maze) => (
                  <TableRow key={maze.id}>
                    <TableCell>
                      <Link to={`/admin/mazes/${maze.id}`} className="flex items-center gap-2 font-medium hover:underline">
                        <MapIcon className="text-muted-foreground size-4" aria-hidden />
                        {maze.name}
                      </Link>
                      <div className="text-muted-foreground font-mono text-xs">
                        {maze.id} · scene {maze.scene_sha256.slice(0, 12)}
                        {maze.source && "seed" in maze.source ? "" : " · imported"}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {maze.grid ? `${maze.grid.rows}×${maze.grid.cols}` : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {maze.camera ? `${maze.camera.width}×${maze.camera.height}` : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={DATASET_LABEL[maze.dataset_state].variant}>
                        {DATASET_LABEL[maze.dataset_state].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {maze.used_by.length ? (
                        <ul className="space-y-0.5">
                          {maze.used_by.map((c) => (
                            <li key={c.id}>
                              <Link to={`/admin/challenges/${c.id}/setup`} className="hover:underline">
                                {c.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted-foreground">nobody</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                      {formatTimestamp(maze.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </AsyncBoundary>

      <NewMazeDialog open={creating} onOpenChange={setCreating} onCreated={() => mazes.refetch()} />
    </div>
  );
}
